import * as React from 'react';
import './TopCategoriesSidebar.css';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import HomeIcon from '@mui/icons-material/Home';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocalGroceryStoreIcon from '@mui/icons-material/LocalGroceryStore';
import CheckroomIcon from '@mui/icons-material/Checkroom';
import PetsIcon from '@mui/icons-material/Pets';
import DevicesIcon from '@mui/icons-material/Devices';
import ChairIcon from '@mui/icons-material/Chair';
import HandymanIcon from '@mui/icons-material/Handyman';
import SportsBasketballIcon from '@mui/icons-material/SportsBasketball';
import YardIcon from '@mui/icons-material/Yard';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import SpaIcon from '@mui/icons-material/Spa';
import DiamondIcon from '@mui/icons-material/Diamond';
import ToysIcon from '@mui/icons-material/Toys';
import ChildFriendlyIcon from '@mui/icons-material/ChildFriendly';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import ComputerIcon from '@mui/icons-material/Computer';
import MovieIcon from '@mui/icons-material/Movie';
import WorkIcon from '@mui/icons-material/Work';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import CategoryIcon from '@mui/icons-material/Category';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

/* Desktop-only sidebar (CSS hides it under 768px). Shows the first N
   categories from the existing API list, click filters the deal list to
   that single category via the `onSelectOne` callback. */
const MAX_VISIBLE = 6;

// Mapped by stable `cat.name` slug from /Categories (lowercased English
// identifier — independent of display name in either locale).
const CATEGORY_META = {
  home:       { Icon: HomeIcon,               en: 'Decor, bedding & more',   fr: 'Décor, literie et plus' },
  kitchen:    { Icon: RestaurantIcon,         en: 'Cookware & appliances',   fr: 'Cuisine et électros' },
  grocery:    { Icon: LocalGroceryStoreIcon,  en: 'Food & pantry essentials', fr: 'Épicerie et essentiels' },
  clothing:   { Icon: CheckroomIcon,          en: 'Fashion for everyone',    fr: 'Mode pour tous' },
  pets:       { Icon: PetsIcon,               en: 'Food, toys & supplies',   fr: 'Nourriture et accessoires' },
  electronic: { Icon: DevicesIcon,            en: 'Tech, gadgets & devices', fr: 'Tech, gadgets, appareils' },
  furniture:  { Icon: ChairIcon,              en: 'Sofas, tables & desks',   fr: 'Sofas, tables, bureaux' },
  tool:       { Icon: HandymanIcon,           en: 'DIY & power tools',       fr: 'Bricolage et outils' },
  sport:      { Icon: SportsBasketballIcon,   en: 'Fitness gear & outdoors', fr: 'Sport et plein air' },
  lawn:       { Icon: YardIcon,               en: 'Garden & outdoor gear',   fr: 'Jardin et extérieur' },
  health:     { Icon: HealthAndSafetyIcon,    en: 'Wellness & personal care', fr: 'Bien-être et soins' },
  beauty:     { Icon: SpaIcon,                en: 'Skincare & cosmetics',    fr: 'Soins et cosmétiques' },
  jewel:      { Icon: DiamondIcon,            en: 'Rings, watches & more',   fr: 'Bijoux et montres' },
  toys:       { Icon: ToysIcon,               en: 'Games & playtime fun',    fr: 'Jouets et jeux' },
  baby:       { Icon: ChildFriendlyIcon,      en: 'Gear & baby care',        fr: 'Bébé et essentiels' },
  videogames: { Icon: SportsEsportsIcon,      en: 'Consoles, games & gear',  fr: 'Consoles et jeux' },
  computer:   { Icon: ComputerIcon,           en: 'Laptops & accessories',   fr: 'Ordinateurs et accessoires' },
  dvddigital: { Icon: MovieIcon,              en: 'Movies & streaming',      fr: 'Films et divertissement' },
  office:     { Icon: WorkIcon,               en: 'Supplies & stationery',   fr: 'Fournitures et papeterie' },
  music:      { Icon: MusicNoteIcon,          en: 'Instruments & audio gear', fr: 'Musique et audio' },
  automotive: { Icon: DirectionsCarIcon,      en: 'Car parts & accessories', fr: 'Auto et accessoires' },
  books:      { Icon: MenuBookIcon,           en: 'Reads, novels & guides',  fr: 'Livres et romans' },
  giftcard:   { Icon: CardGiftcardIcon,       en: 'Gift cards for everyone', fr: 'Cartes-cadeaux variées' },
  other:      { Icon: CategoryIcon,           en: 'Browse all deals',        fr: 'Tous les autres deals' },
};

const FALLBACK_META = { Icon: CategoryIcon, en: 'Browse deals', fr: 'Voir les deals' };

function metaFor(cat) {
  const key = (cat && cat.name ? String(cat.name) : '').toLowerCase().trim();
  return CATEGORY_META[key] || FALLBACK_META;
}

export default function TopCategoriesSidebar({ categories, selected, onSelectOne }) {
  const { t } = useTranslation();
  const fr = i18n.language === 'fr';

  const top = Array.isArray(categories) ? categories.slice(0, MAX_VISIBLE) : [];
  if (top.length === 0) return <aside className="top-categories" aria-hidden="true" />;

  const isSingleSelected = (cat) =>
    Array.isArray(selected) && selected.length === 1 && selected[0] === cat.id;

  return (
    <aside
      className="top-categories"
      aria-label={t('Top_Categories_Title', { defaultValue: fr ? 'Catégories en vedette' : 'Top Categories' })}
    >
      <h3 className="top-categories-title">
        {t('Top_Categories_Title', { defaultValue: fr ? 'Catégories en vedette' : 'Top Categories' })}
      </h3>
      <ul className="top-categories-list">
        {top.map((cat) => {
          const active = isSingleSelected(cat);
          const { Icon, en, fr: frSub } = metaFor(cat);
          return (
            <li key={cat.id}>
              <button
                type="button"
                className={'top-categories-item' + (active ? ' is-active' : '')}
                onClick={() => onSelectOne && onSelectOne(cat.id)}
              >
                <span className="top-categories-icon" aria-hidden="true">
                  <Icon fontSize="inherit" />
                </span>
                <span className="top-categories-text">
                  <span className="top-categories-name">
                    {fr ? cat.nameFr : cat.nameEn}
                  </span>
                  <span className="top-categories-sub">
                    {fr ? frSub : en}
                  </span>
                </span>
                <ChevronRightIcon className="top-categories-chevron" aria-hidden="true" />
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
